import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { FaireProtectionTooltip } from "@faire/retailer/components/BrandCartCard/components/FaireProtectionTooltip";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import {
  isDiscontinued as isProductOptionDiscontinued,
  isProductOptionInStock,
} from "@faire/retailer/lib/productOptions";
import {
  areRequiredProductCustomizationsApplied,
  areAppliedProductCustomizationsValid,
} from "@faire/retailer/services/ProductCustomization";
import { useSettingChangeBagToCartCopyAndIcon } from "@faire/retailer/settings/useSettingChangeBagToCartCopyAndIcon";
import { ProductAddToCartState } from "@faire/retailer/views/product/ProductDetails/AddToCart/ProductAddToCartState";

interface IProps {
  brandToken: string | undefined;
  addToCartNotificationInProgress: boolean;
  isBrandActive: boolean | undefined;
  isInvoice: boolean | undefined;
  isCartInfoLoading: boolean | undefined;
  isFaireProtectionLoading: boolean;
  productAddToCartState: ProductAddToCartState;
  product: IProductPageResponse.IPdpProduct;
  productAddCTAOverride?: (
    isUpdating: boolean,
    strictLocalize: StrictLocalizeFunction
  ) => string;
  updateCart: () => Promise<void>;
  isBrandTokenForbidden: (
    brandToken: string,
    options?:
      | {
          trackReason?: true | undefined;
        }
      | undefined
  ) => boolean;
}

const AddToCartButtonBase: React.FC<IProps> = ({
  brandToken,
  isInvoice,
  isBrandActive,
  addToCartNotificationInProgress,
  isCartInfoLoading,
  isFaireProtectionLoading,
  productAddToCartState,
  product,
  productAddCTAOverride,
  updateCart,
  isBrandTokenForbidden,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const changeBagToCart = useSettingChangeBagToCartCopyAndIcon();
  const addToBagText = getAddToBagText(
    addToCartNotificationInProgress,
    product,
    isBrandActive,
    productAddToCartState,
    changeBagToCart,
    strictLocalize,
    productAddCTAOverride
  );

  if (!addToBagText) {
    return null;
  }

  const brandTokenForbidden = isBrandTokenForbidden(brandToken ?? "");
  const disableButton = addToCartDisabled(
    product,
    productAddToCartState,
    isBrandActive,
    isInvoice
  );

  if (brandTokenForbidden && !isCartInfoLoading) {
    return (
      <CtaContainer>
        <FaireProtectionTooltip
          hidden={false}
          targetNode={
            <AddToCart
              disabled
              onClick={updateCart}
              data-test-id="addToCartButton"
            >
              {addToBagText}
            </AddToCart>
          }
        />
      </CtaContainer>
    );
  }

  return (
    <AddToCart
      disabled={disableButton}
      loading={isCartInfoLoading || isFaireProtectionLoading}
      onClick={updateCart}
      data-test-id="addToCartButton"
    >
      {addToBagText}
    </AddToCart>
  );
};

export const AddToCartButton = observer(AddToCartButtonBase);

const isSelectedProductDifferentThanCart = (
  productAddToCartState: ProductAddToCartState
) => {
  const {
    selectedQuantity,
    productOptionInCart,
    includesTesters,
    isSelectedOpenSizingQtyModified,
    isOpenSizingVariant,
  } = productAddToCartState;

  const isProductOptionModified = isOpenSizingVariant
    ? isSelectedOpenSizingQtyModified
    : selectedQuantity !== productOptionInCart?.quantity ||
      includesTesters !== !!productOptionInCart?.includes_tester;

  return !productOptionInCart || isProductOptionModified;
};

const addToCartDisabled = (
  product: IProductPageResponse.IPdpProduct,
  productAddToCartState: ProductAddToCartState,
  isBrandActive: boolean | undefined,
  isInvoice: boolean | undefined
) => {
  const {
    selectedOption,
    selectedQuantity,
    customizations,
    updateQuantityOnChange,
    perStyleOptionVariations,
    exceedMaxAvailableUnits,
  } = productAddToCartState;
  const canAdd = isInvoice || canAddToCart();

  const enableEditProductQuantity = updateQuantityOnChange
    ? isSelectedProductDifferentThanCart(productAddToCartState)
    : selectedQuantity !== 0 && !exceedMaxAvailableUnits;

  if (selectedOption === undefined) {
    return true;
  }

  return (
    // Don't include brand since we handle online ony restricted elsewhere
    !canAdd ||
    !isBrandActive ||
    isProductOptionDiscontinued(product, selectedOption) ||
    !enableEditProductQuantity ||
    !isProductOptionInStock(selectedOption, perStyleOptionVariations) ||
    !areRequiredProductCustomizationsApplied(
      product.purchase_information?.customizations || [],
      customizations
    ) ||
    !areAppliedProductCustomizationsValid(
      productAddToCartState.customizationsInMoqCalculation,
      customizations
    )
  );
};

const getAddToBagText = (
  addToCartNotificationInProgress: boolean,
  product: IProductPageResponse.IPdpProduct,
  isBrandActive: boolean | undefined,
  productAddToCartState: ProductAddToCartState,
  changeBagToCart: boolean,
  strictLocalize: StrictLocalizeFunction,
  productAddCTAOverride?:
    | ((isUpdating: boolean, strictLocalize: StrictLocalizeFunction) => string)
    | undefined
) => {
  const {
    selectedOption,
    updateQuantityOnChange,
    productOptionInCart,
    perStyleOptionVariations,
    isPreorderableSelected,
  } = productAddToCartState;

  if (
    selectedOption === undefined ||
    !isBrandActive ||
    isProductOptionDiscontinued(product, selectedOption)
  ) {
    return strictLocalize({
      defaultMessage: "Unavailable",
      description: { text: "Not available for purchase" },
    });
  }

  if (!isProductOptionInStock(selectedOption, perStyleOptionVariations)) {
    return null;
  }

  if (productAddCTAOverride) {
    const isUpdating = !!(updateQuantityOnChange && productOptionInCart);
    return productAddCTAOverride(isUpdating, strictLocalize);
  }

  const isRemovedFromCart =
    updateQuantityOnChange &&
    !productOptionInCart &&
    addToCartNotificationInProgress;

  if (changeBagToCart) {
    return getAddToCartText(
      updateQuantityOnChange && !!productOptionInCart,
      isSelectedProductDifferentThanCart(productAddToCartState),
      isPreorderableSelected,
      addToCartNotificationInProgress,
      isRemovedFromCart,
      strictLocalize
    );
  }

  if (updateQuantityOnChange && productOptionInCart) {
    if (isSelectedProductDifferentThanCart(productAddToCartState)) {
      return addToCartNotificationInProgress
        ? strictLocalize({
            defaultMessage: "Updated Bag",
            description: {
              text: "Confirmation that the bag has been updated with new products",
            },
          })
        : strictLocalize({
            defaultMessage: "Update Bag",
            description: {
              text: "The bag will be updated with new products",
            },
          });
    }

    return strictLocalize({
      defaultMessage: "In Bag",
      description: { text: "The product already exists in your bag" },
    });
  }

  if (isPreorderableSelected) {
    return addToCartNotificationInProgress
      ? strictLocalize({
          defaultMessage: "Added to Bag",
          description: { text: "The product(s) have been added to your bag" },
        })
      : strictLocalize({
          defaultMessage: "Preorder",
          description: { text: "Will preorder the selected product" },
        });
  }

  return isRemovedFromCart || !addToCartNotificationInProgress
    ? strictLocalize({
        defaultMessage: "Add to Bag",
        description: { text: "Prompt to add new product(s) to your bag" },
      })
    : strictLocalize({
        defaultMessage: "Added to Bag!",
        description: { text: "The product(s) have been added to your bag" },
      });
};

const getAddToCartText = (
  updateProductInCart: boolean,
  isSelectedProductDifferentThanCart: boolean,
  isPreorderableSelected: boolean,
  addToCartNotificationInProgress: boolean,
  isRemovedFromCart: boolean,
  strictLocalize: StrictLocalizeFunction
) => {
  if (updateProductInCart) {
    return isSelectedProductDifferentThanCart
      ? strictLocalize({
          defaultMessage: "Update cart",
          description: {
            text: "The cart will be updated with new products",
            img: "https://cdn.faire.com/fastly/ede640252a976d4f9dd697768286b5b300ab79c7e50344f7ca1ebd6309b8133d.png",
          },
        })
      : strictLocalize({
          defaultMessage: "In cart",
          description: {
            text: "The product already exists in your cart",
            img: "https://cdn.faire.com/fastly/78bfcb6759b477f620bfb8d0f2d17aa7b8d52ee8ab167c20f1fe01b85813b5d9.png",
          },
        });
  }

  if (isPreorderableSelected) {
    return addToCartNotificationInProgress
      ? strictLocalize({
          defaultMessage: "Added to cart",
          description: {
            text: "The product(s) have been added to your cart",
            img: "https://cdn.faire.com/fastly/8885d0f613ed64b0d5afae2c034023a977ca8db9ca61f71914ca44815516dbd8.png",
          },
        })
      : strictLocalize({
          defaultMessage: "Preorder",
          description: {
            text: "Will preorder the selected product",
            img: "https://cdn.faire.com/fastly/10c8594d220c55a57268f316b5f30ced1032616bbb0d1c313284cd65628e20d6.png",
          },
        });
  }

  return isRemovedFromCart || !addToCartNotificationInProgress
    ? strictLocalize({
        defaultMessage: "Add to cart",
        description: {
          text: "Prompt to add new product(s) to your cart",
          img: "https://cdn.faire.com/fastly/600cee2f244e58ca7b730bbe0212384e3b18973af0efa234955e3030f9d6cdc3.png",
        },
      })
    : strictLocalize({
        defaultMessage: "Added to cart!",
        description: {
          text: "The product(s) have been added to your cart",
          img: "https://cdn.faire.com/fastly/984385cd83fd21ce257b76fd00fab2a975aa674c565b1fee1dc08d3e69e85aff.png",
        },
      });
};

const CtaContainer = styled(Column)`
  width: 100%;
`;

const AddToCart = styled(Button)`
  width: 100%;
`;
